import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { theme } from "antd";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import type { AnalyticsActivityPoint } from "../../api";

/**
 * How much work started and how much finished, over the chosen window.
 *
 * The one chart on this page, because it is the one question a time series
 * answers better than a number: *is the work piling up?* Two lines that keep
 * pace mean a process in balance; started running above completed for weeks is
 * a backlog forming, and no KPI card shows that.
 *
 * Decisions that are reasoning, not taste:
 *
 * * **Two series, one axis.** Both are counts of cases, so they share a scale
 *   honestly. A second y-axis would let the two lines be slid past each other
 *   until they told whatever story the scaling chose — the single worst thing a
 *   chart can do, and the reason this stays one axis even when the counts are
 *   far apart.
 * * **Identity is never carried by colour alone.** The panel header holds a
 *   labelled key, the shared tooltip names both series on every hover, and the
 *   two marks differ in *form* — started is a filled area, completed a line
 *   with markers — so the chart still reads in greyscale or with any colour
 *   vision. End-of-line labels were tried and dropped: on a quiet process both
 *   series finish within a pixel of each other and the labels overlap.
 * * **The colours are validated, not chosen.** `#2a78d6` is the categorical
 *   slot-1 blue this app already plots reports in; `#008300` is the palette's
 *   green, and the pair clears the colour-vision and contrast gates against a
 *   white card. Green for *completed* is the one place a hue may carry meaning
 *   here, and it is the meaning everybody already reads it as.
 * * **Empty buckets are plotted.** The server returns every day/week/month in
 *   the range including the zeros, so a quiet week shows as a dip rather than
 *   being closed up into a slope that implies work nobody did.
 * * **Straight segments, never a spline.** This was drawn with `areaspline`
 *   first, and looking at it in a browser is what killed it: a smoothed curve
 *   through 4, 0, 3 bulges *above* 4 and sags *below* 0, so the chart claimed
 *   fractional cases on days when nothing at all happened. Counts are discrete
 *   and the interpolation between two of them is a fiction; a straight segment
 *   at least admits it is one.
 */

/** Validated categorical slot 1 — the same blue the report charts use. */
const STARTED = "#2a78d6";
/** The palette's green: >= 3:1 on a white card, and clear of the blue under CVD. */
const COMPLETED = "#008300";

export const ACTIVITY_COLORS = { started: STARTED, completed: COMPLETED };

const CHART_HEIGHT = 280;

const ActivityChart = ({
  activity,
  bucket,
}: {
  activity: AnalyticsActivityPoint[];
  bucket: "day" | "week" | "month";
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const options = useMemo<Highcharts.Options>(() => {
    const axisText = {
      color: token.colorTextTertiary,
      fontSize: "11px",
      fontVariantNumeric: "tabular-nums",
    };
    const bucketWord =
      bucket === "month" ? t("month") : bucket === "week" ? t("week") : t("day");

    return {
      chart: {
        type: "area",
        backgroundColor: "transparent",
        height: CHART_HEIGHT,
        spacing: [8, 4, 0, 0],
        style: {
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      // The key lives in the panel header, beside the title it belongs to,
      // rather than as a box the chart has to make room for.
      legend: { enabled: false },
      xAxis: {
        categories: activity.map((point) => point.label),
        lineWidth: 0,
        tickLength: 0,
        gridLineWidth: 0,
        // Highcharts thins category labels on its own; letting it also rotate
        // them turns a 30-day axis into a diagonal fringe.
        labels: { style: axisText, autoRotation: undefined, rotation: 0 },
        crosshair: { width: 1, color: token.colorSplit },
      },
      yAxis: {
        title: { text: undefined },
        gridLineWidth: 1,
        gridLineColor: token.colorSplit,
        gridLineDashStyle: "Solid",
        lineWidth: 0,
        allowDecimals: false, // cases are whole things
        labels: { style: axisText },
      },
      plotOptions: {
        series: {
          animation: { duration: 240 },
          states: { hover: { brightness: 0.06 }, inactive: { opacity: 1 } },
          marker: { enabled: false, symbol: "circle" },
        },
      },
      tooltip: {
        shared: true, // both numbers for the same date, in one box
        backgroundColor: token.colorBgElevated,
        borderColor: token.colorBorderSecondary,
        borderRadius: 8,
        borderWidth: 1,
        shadow: false,
        padding: 10,
        style: { color: token.colorText, fontSize: "12px" },
        headerFormat:
          '<span style="font-size:11px;opacity:.65">{point.key}</span><br/>',
        pointFormat:
          '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
      },
      series: [
        {
          type: "area",
          name: t("Started"),
          data: activity.map((point) => point.created),
          color: STARTED,
          lineWidth: 2,
          fillOpacity: 0.1,
          zIndex: 1,
        },
        {
          type: "line",
          name: t("Completed"),
          data: activity.map((point) => point.completed),
          color: COMPLETED,
          lineWidth: 2,
          zIndex: 2,
          marker: {
            enabled: activity.length <= 14,
            radius: 3.5,
            lineWidth: 2,
            lineColor: token.colorBgContainer,
          },
        },
      ] as Highcharts.SeriesOptionsType[],
      accessibility: { description: t("Cases started and completed per {{bucket}}", { bucket: bucketWord }) },
    };
  }, [activity, bucket, token, t]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      // Re-mount when the bucketing changes rather than mutating a live chart,
      // so a stale axis never survives a change of range.
      key={`${bucket}-${activity.length}`}
    />
  );
};

export default ActivityChart;
